import React from 'react'

import {
  Banner,
  BannerDescription,
  BannerEyebrow,
  BannerFootnote,
  BannerImage,
  BannerImageContainer,
  BannerLinks,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Button,
  FluidImage,
  Icon,
  List,
  ListItem,
  P,
  Typography,
} from '@vp/swan'

import { WIX_PRIVACY_POLICY_PAGER_URL, WIX_TERMS_OF_USE_PAGER_URL } from '@vp/digital-url-service-lib-ubik'
import { isProduction } from '@vp/digital-environment-lib-ubik'
import { LinkFactory, TokenReplacer } from '@vp/digital-token-replacer-lib-ubik'

import { GET_STARTED_CTA_DETAIL, MAIN_HERO_DATA_SECTION_LABEL, trackMainHeroLinkClick } from './websiteAnalytics'

import MissingContentAlert from '../MissingContentAlert'
import { useStyles } from '@vp/ubik-context'
import styles, { stylesheet } from './WebsitesHeroSection.scss'
import getResponsiveCloudinaryImage, { getCloudinaryImage } from '../helpers/cloudinaryImageResizerHelper'
import Markdown from 'markdown-to-jsx'

interface HeroSectionProps {
  content: any
  pageContext: any
  isWebsShutdown?: boolean
  handleGetStartedNavigation?: () => void
}

const HeroSection = ({ content, pageContext, isWebsShutdown, handleGetStartedNavigation }: HeroSectionProps) => {
  if (!content) {
    return <MissingContentAlert />
  }
  useStyles(stylesheet)

  const { urlWixPartnerGetStartedTnCAcceptedUrls } = pageContext

  const urlWixPartnerGetStartedTncAcceptedUrl = isProduction() ? urlWixPartnerGetStartedTnCAcceptedUrls.prod : urlWixPartnerGetStartedTnCAcceptedUrls.qa

  return (
    <Banner mb={{ sm: 5, md: 5, lg: 7, xl: 7 }} bgc="standard" height="short" pr={{ sm: 0, md: 0, lg: 0, xl: 0 }}>
      <HeroImage className={styles.wixWebsitesPdpHeroImageMobile} isWebsShutdown={isWebsShutdown} mainImage={content.media[0]} />

      <BannerTextContainer>
        <BannerText>
          <BannerEyebrow>
            <FluidImage
              className={styles.wixWebsitesPdpHeroLogo}
              srcSet={getResponsiveCloudinaryImage(content.media[1].mediaLink[0].secure_url)}
              src={getCloudinaryImage(content.media[1].mediaLink[0].secure_url)}
              alt={content.media[1].altText}
              sizes="170px"
            />
          </BannerEyebrow>

          <BannerTitle component="h1" fontSize="x4large">
            {content.title}
          </BannerTitle>

          <BannerDescription component="p">{content.description}</BannerDescription>

          <BannerDescription mt={6} textAlign="left">
            <P fontWeight="bold" fontSize="large">
              {content.heroFeatures.title}
            </P>

            <List skin="minimal" mt={4}>
              {content.heroFeatures.features.map((item: { name: string; description: string }) => (
                <ListItem key={item.name ?? item.description}>
                  <Icon iconType="check" size="24p" skin="standard" mr={4} />
                  {item.name ?? <Markdown>{item.description}</Markdown>}
                </ListItem>
              ))}
            </List>
          </BannerDescription>

          <BannerLinks>
            <Button
              skin="secondary"
              iconPosition="right"
              data-position="1"
              data-translation={GET_STARTED_CTA_DETAIL}
              data-section={MAIN_HERO_DATA_SECTION_LABEL}
              className={styles.wixWebsitesPdpHeroCta}
              onClick={() => {
                if (!isWebsShutdown) {
                  trackMainHeroLinkClick(urlWixPartnerGetStartedTncAcceptedUrl)
                }
                handleGetStartedNavigation()
              }}
            >
              {content.actions[0].name}
              <Icon iconType="arrowRight" size="20p" />
            </Button>
          </BannerLinks>

          <BannerFootnote textColor="info" className={styles.wixWebsitesPdpHeroFooter}>
            <Typography as="span">
              <TokenReplacer
                template={content.heroMetadata[0].description}
                replacements={{
                  link: LinkFactory({
                    wixTermsOfUsePagerUrl: WIX_TERMS_OF_USE_PAGER_URL,
                    wixPrivacyPolicyPagerUrl: WIX_PRIVACY_POLICY_PAGER_URL,
                  }),
                }}
                convertMarkdown
              />
            </Typography>
          </BannerFootnote>
        </BannerText>
      </BannerTextContainer>

      <HeroImage className={styles.wixWebsitesPdpHeroImageDesktop} isWebsShutdown={isWebsShutdown} mainImage={content.media[0]} />
    </Banner>
  )
}

interface HeroImageProps {
  className: string
  isWebsShutdown: boolean
  mainImage: any
}

const HeroImage = ({ className, mainImage }: HeroImageProps) => {
  return (
    <BannerImageContainer className={className}>
      <BannerImage className={styles.wixWebsitesPdpHeroImage}>
        <FluidImage
          alt={mainImage.altText ?? ''}
          srcSet={getResponsiveCloudinaryImage(mainImage.mediaLink[0].secure_url)}
          src={getCloudinaryImage(mainImage.mediaLink[0].secure_url)}
          sizes="(max-width: 768px) 100vw,
            (max-width: 2840px) 50vw,
            1420px"
        />
      </BannerImage>
    </BannerImageContainer>
  )
}

export default HeroSection
